import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import apiFetch from '../util/fetch-utils';

export default function UriQuickSearch({ currentUri }) {
    const { workspaceNumber } = useParams();
    const history = useHistory();
    const [open, setOpen] = useState(false);

    // Keep the last known URI so the bar never blanks during loading transitions
    const lastUriRef = useRef(currentUri);
    if (currentUri) lastUriRef.current = currentUri;
    const displayUri = lastUriRef.current;

    const [flashKey, setFlashKey] = useState(0);
    const mountedRef = useRef(false);
    useEffect(() => {
        if (!mountedRef.current) { mountedRef.current = true; return; }
        setFlashKey(k => k + 1);
    }, [currentUri]);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const debounceRef = useRef(null);
    const inputRef = useRef(null);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (open) inputRef.current?.focus();
    }, [open]);

    useEffect(() => {
        function onKeyDown(e) {
            if (e.ctrlKey && e.key === 'g') {
                e.preventDefault();
                setOpen(true);
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        function onMouseDown(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
        }
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, []);

    function close() {
        setOpen(false);
        setQuery('');
        setResults([]);
        setActiveIndex(-1);
        clearTimeout(debounceRef.current);
    }

    function handleChange(e) {
        const val = e.target.value;
        setQuery(val);
        setActiveIndex(-1);
        clearTimeout(debounceRef.current);
        if (!val.trim()) { setResults([]); return; }
        debounceRef.current = setTimeout(async () => {
            const qs = new URLSearchParams({ workspaceNumber, uriSearch: `query:${encodeURIComponent(val.trim())}`, size: 8, page: 0 });
            const res = await apiFetch(`/api/monitored-uris/search/searchMonitoredUri?${qs}`);
            if (!res.ok) return;
            const data = await res.json();
            setResults(data._embedded?.monitoredUris || []);
        }, 300);
    }

    function select(item) {
        history.push(`/ws/${workspaceNumber}/monitored-uris/${item.id}`);
        close();
    }

    function handleKeyDown(e) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            select(results[activeIndex]);
        } else if (e.key === 'Escape') {
            close();
        }
    }

    return (
        <Wrap ref={wrapRef}>
            <TriggerRow>
                <AddressBar $open={open} onClick={() => setOpen(o => !o)} title="Switch URL (Ctrl+G)">
                    {flashKey > 0 && <FlashOverlay key={flashKey} />}
                    <AddressText>{displayUri}</AddressText>
                    <SwitchHint $open={open}>jump to another URL</SwitchHint>
                </AddressBar>
                <ExternalLink href={displayUri} target="_blank" rel="noopener noreferrer" title="Open in new tab">
                    <OpenInNewIcon style={{ fontSize: '0.9rem' }} />
                </ExternalLink>
            </TriggerRow>
            {open && (
                <Dropdown>
                    <DropdownHeader>
                        Jump to a URL in this workspace
                        <Shortcut>Ctrl+G</Shortcut>
                    </DropdownHeader>
                    <SearchRow>
                        <SearchInput
                            ref={inputRef}
                            value={query}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Type to search URLs…"
                        />
                    </SearchRow>
                    {results.length > 0 && (
                        <Results>
                            {results.map((item, i) => (
                                <Item
                                    key={item.id}
                                    $active={i === activeIndex}
                                    $current={item.uri === currentUri}
                                    onMouseDown={() => select(item)}
                                    onMouseEnter={() => setActiveIndex(i)}
                                >
                                    {item.uri}
                                </Item>
                            ))}
                        </Results>
                    )}
                </Dropdown>
            )}
        </Wrap>
    );
}

const Wrap = styled.div`
    position: relative;
    min-width: 0;
    flex: 1;
`;

const TriggerRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const ExternalLink = styled.a`
    flex-shrink: 0;
    color: #cbd5e1;
    text-decoration: none;
    transition: color 120ms;
    &:hover { color: #6366f1; }
`;

const AddressBar = styled.button`
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    background: #f1f5f9;
    border: 1px solid ${({ $open }) => $open ? '#a5b4fc' : '#e2e8f0'};
    border-radius: 8px;
    padding: 6px 10px;
    cursor: pointer;
    text-align: left;
    transition: background 120ms, border-color 120ms, box-shadow 120ms;
    &:hover {
        background: #eef2ff;
        border-color: #a5b4fc;
    }
    ${({ $open }) => $open && `background: #eef2ff; box-shadow: 0 0 0 3px #e0e7ff;`}
`;

const AddressText = styled.span`
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    word-break: break-all;
    line-height: 1.5;
    min-width: 0;
    flex: 1;
`;

const SwitchHint = styled.span`
    flex-shrink: 0;
    font-size: 0.65rem;
    font-weight: 600;
    color: ${({ $open }) => $open ? '#6366f1' : '#94a3b8'};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    white-space: nowrap;
    transition: color 120ms;
    ${AddressBar}:hover & { color: #6366f1; }
`;

const FlashOverlay = styled.span`
    position: absolute;
    inset: 0;
    border-radius: 8px;
    pointer-events: none;
    background: #ede9fe;
    animation: flashFade 0.7s ease-out forwards;
    @keyframes flashFade {
        0%   { opacity: 0.75; }
        100% { opacity: 0; }
    }
`;

const Dropdown = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.1);
    z-index: 100;
    overflow: hidden;
`;

const DropdownHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px 8px;
    font-size: 0.72rem;
    font-weight: 600;
    color: #64748b;
    border-bottom: 1px solid #f1f5f9;
`;

const Shortcut = styled.span`
    margin-left: auto;
    font-size: 0.65rem;
    font-weight: 500;
    color: #94a3b8;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 1px 5px;
`;

const SearchRow = styled.div`
    padding: 10px 12px;
    border-bottom: 1px solid #f1f5f9;
`;

const SearchInput = styled.input`
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 0.82rem;
    outline: none;
    color: #0f172a;
    background: #f8fafc;
    &:focus { border-color: #a5b4fc; background: #fff; }
    &::placeholder { color: #94a3b8; }
`;

const Results = styled.div`
    max-height: 260px;
    overflow-y: auto;
`;

const Item = styled.div`
    padding: 8px 12px;
    font-size: 0.8rem;
    color: ${({ $current }) => $current ? '#6366f1' : '#0f172a'};
    font-weight: ${({ $current }) => $current ? 600 : 400};
    cursor: pointer;
    word-break: break-all;
    background: ${({ $active }) => $active ? '#eef2ff' : 'transparent'};
    &:not(:last-child) { border-bottom: 1px solid #f1f5f9; }
    &:hover { background: #eef2ff; }
`;
